/**
 * 商品カードのスクロール連動フェードアップ
 * （商品一覧 / RELATED PRODUCTS / RECENTLY VIEWED 共通）
 *
 * TOP FEATURED（top-new-arrivals-section.js）と同じ IntersectionObserver 方式。
 * - 初めてビューポートへ入った行のカードに .is-visible を付けて表示する
 * - 一度表示したカードは元へ戻さない（unobserve する）
 * - 同じ行のカードは左→右へごく短い時間差（実測座標ベース・列数に依存しない）
 * - 対象は [data-card-reveal-list] の中の
 *   .collection-product-card[data-collection-reveal]
 * - 非同期で描画されるセクション（RELATED PRODUCTS / RECENTLY VIEWED）や
 *   Filter / Sort の差し替えは [data-card-reveal-root] を含むセクションの
 *   DOM 変化で検知する
 * - prefers-reduced-motion: reduce と IntersectionObserver 非対応時は即時表示
 */
(function () {
  /*
   * RELATED PRODUCTS と RECENTLY VIEWED の両セクションが同じページに並ぶと
   * このファイルの script タグが 2 つ出力される。二重初期化
   * （observer の重複生成・shopify:section:load の二重登録）を防ぐ。
   */
  if (window.__collectionCardRevealLoaded) {
    return;
  }
  window.__collectionCardRevealLoaded = true;

  var LIST_SELECTOR = '[data-card-reveal-list]';
  var ROOT_SELECTOR = '[data-card-reveal-list], [data-card-reveal-root]';
  var CARD_SELECTOR = '.collection-product-card[data-collection-reveal]';
  var VISIBLE_CLASS = 'is-visible';
  var STAGGER_STEP = 0.08; /* 秒: 同じ行の 1 枚あたりの遅延 */
  var STAGGER_MAX = 3; /* 遅延の上限段数（最大 0.24 秒）。商品数が増えても伸びない */
  var ROW_TOLERANCE = 4; /* px: 同じ行と見なす上端のズレ */

  var prefersReducedMotion =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function showAll(root) {
    root.querySelectorAll(CARD_SELECTOR).forEach(function (card) {
      card.classList.add(VISIBLE_CLASS);
    });
  }

  /**
   * 同じ行（＝同じ offsetTop）のカードをまとめて左→右の順に表示する。
   * 実測座標で行を判定するため、2列 / 3列 / 4列でも、
   * 横スクロールカルーセル（1 行に全カード）でも同じロジックで成立する。
   */
  function revealRow(card, observer) {
    var list = card.closest(LIST_SELECTOR);

    if (!list) {
      card.classList.add(VISIBLE_CLASS);
      return;
    }

    var rowTop = card.offsetTop;
    var row = [];

    list.querySelectorAll(CARD_SELECTOR).forEach(function (sibling) {
      if (sibling.classList.contains(VISIBLE_CLASS)) {
        return;
      }
      if (Math.abs(sibling.offsetTop - rowTop) > ROW_TOLERANCE) {
        return;
      }
      row.push(sibling);
    });

    row.sort(function (a, b) {
      return a.offsetLeft - b.offsetLeft;
    });

    row.forEach(function (sibling, index) {
      if (observer) {
        observer.unobserve(sibling);
      }
      sibling.style.transitionDelay = Math.min(index, STAGGER_MAX) * STAGGER_STEP + 's';
      sibling.classList.add(VISIBLE_CLASS);
    });
  }

  function onIntersect(entries, observer) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) {
        return;
      }

      observer.unobserve(entry.target);

      if (entry.target.classList.contains(VISIBLE_CLASS)) {
        return;
      }

      revealRow(entry.target, observer);
    });
  }

  function observeCards(list, observer) {
    list.querySelectorAll(CARD_SELECTOR).forEach(function (card) {
      if (card.classList.contains(VISIBLE_CLASS) || card.dataset.cardRevealBound === 'true') {
        return;
      }
      card.dataset.cardRevealBound = 'true';
      observer.observe(card);
    });
  }

  function initList(list) {
    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      showAll(list);
      return;
    }

    if (!list._cardRevealObserver) {
      list._cardRevealObserver = new IntersectionObserver(onIntersect, {
        root: null,
        threshold: 0.12,
        /* 画面下端ぎりぎりで出し始めない（FEATURED と同じ考え方） */
        rootMargin: '0px 0px -120px 0px'
      });

      /* 同じリスト内でカードだけが差し替わる場合に備える */
      new MutationObserver(function () {
        observeCards(list, list._cardRevealObserver);
      }).observe(list, { childList: true });
    }

    observeCards(list, list._cardRevealObserver);
  }

  function initAll(scope) {
    var root = scope || document;

    if (root.matches && root.matches(LIST_SELECTOR)) {
      initList(root);
    }

    root.querySelectorAll(LIST_SELECTOR).forEach(initList);
  }

  /**
   * 商品カードを持つ（または後から持つ）セクションだけを監視する。
   * - RELATED PRODUCTS / RECENTLY VIEWED: fetch 後の innerHTML 差し替え
   * - 商品一覧: Filter / Sort によるセクション差し替え
   * document 全体は監視しない。
   */
  function watchSections() {
    var sections = [];

    document.querySelectorAll(ROOT_SELECTOR).forEach(function (element) {
      var section = element.closest('.shopify-section') || element;

      if (sections.indexOf(section) === -1) {
        sections.push(section);
      }
    });

    sections.forEach(function (section) {
      if (section._cardRevealSectionObserver) {
        return;
      }

      var scheduled = false;

      section._cardRevealSectionObserver = new MutationObserver(function () {
        if (scheduled) {
          return;
        }
        scheduled = true;
        window.requestAnimationFrame(function () {
          scheduled = false;
          initAll(section);
        });
      });

      section._cardRevealSectionObserver.observe(section, { childList: true, subtree: true });
    });
  }

  function start() {
    initAll();
    watchSections();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  /* テーマエディタでのセクション再読み込み */
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
    watchSections();
  });
})();
